import React, { useState, useEffect } from 'react';
import { Save, Building, Phone, Mail, Clock, ArrowLeft, MapPin, Plus, Trash2, Edit2, X, Check, AlertCircle, DollarSign, Hash, User, Globe, CreditCard } from 'lucide-react';
import { SiteSettings } from '../types';

interface AdminSettingsProps {
  settings: SiteSettings;
  onUpdateSettings: (newSettings: SiteSettings) => void;
  onNavigate: (page: string) => void;
}

interface BankDetail {
  id?: number;
  bank_name: string;
  account_number: string;
  account_name: string;
  branch?: string;
  swift_code?: string;
  routing_number?: string;
  currency: string;
  instructions?: string;
  is_active: boolean;
  is_default: boolean;
}

const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? 'https://sparklinggems.shop/api'
  : 'http://localhost/sparkling-gems-backend-php/api';

// Admin key - match your API key
const ADMIN_KEY = 'sparkling-gems-admin-2026';

const AdminSettings: React.FC<AdminSettingsProps> = ({ settings, onUpdateSettings, onNavigate }) => {
  const [formData, setFormData] = useState<SiteSettings>(settings);
  const [isSaving, setIsSaving] = useState(false);
  
  // Bank details state
  const [banks, setBanks] = useState<BankDetail[]>([]);
  const [loadingBanks, setLoadingBanks] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  // Bank form state
  const [bankForm, setBankForm] = useState<Partial<BankDetail>>({
    bank_name: '',
    account_number: '',
    account_name: '',
    branch: '',
    swift_code: '',
    routing_number: '',
    currency: 'PHP',
    instructions: '',
    is_active: true,
    is_default: false
  });

  // Fetch bank details on mount
  useEffect(() => {
    fetchBanks();
  }, []);

  const fetchBanks = async () => {
    setLoadingBanks(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/bank-details/get.php`);
      const data = await response.json();
      
      if (data.success) {
        setBanks(data.banks || []);
      } else {
        setError(data.error || 'Failed to fetch bank details');
      }
    } catch (error) {
      setError('Error connecting to server');
      console.error('Error:', error);
    } finally {
      setLoadingBanks(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleBankInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setBankForm({
      ...bankForm,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    });
  };

  const resetBankForm = () => {
    setBankForm({
      bank_name: '',
      account_number: '',
      account_name: '',
      branch: '',
      swift_code: '',
      routing_number: '',
      currency: 'PHP',
      instructions: '',
      is_active: true,
      is_default: false
    });
    setEditingId(null);
    setShowAddForm(false);
  };

  const handleEditBank = (bank: BankDetail) => {
    setBankForm(bank);
    setEditingId(bank.id || null);
    setShowAddForm(true);
  };

  const handleDeleteBank = async (id: number) => {
    if (!confirm('Are you sure you want to delete this bank account?')) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/bank-details/update.php?id=${id}`, {
        method: 'DELETE',
        headers: {
          'X-Admin-Key': ADMIN_KEY
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSuccessMessage('Bank deleted successfully');
        fetchBanks();
        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        setError(data.error || 'Failed to delete bank');
      }
    } catch (error) {
      setError('Error deleting bank');
    }
  };

  const handleBankSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate required fields
    if (!bankForm.bank_name || !bankForm.account_number || !bankForm.account_name) {
      setError('Bank name, account number, and account name are required');
      return;
    }

    try {
      const payload = editingId ? { ...bankForm, id: editingId } : bankForm;
      
      const response = await fetch(`${API_BASE_URL}/bank-details/update.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Admin-Key': ADMIN_KEY
        },
        body: JSON.stringify(payload)
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSuccessMessage(editingId ? 'Bank updated successfully' : 'Bank added successfully');
        resetBankForm();
        fetchBanks();
        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        setError(data.error || 'Failed to save bank');
      }
    } catch (error) {
      setError('Error saving bank');
    }
  };

  // FIXED: Save site settings to database
  const handleSiteSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_BASE_URL}/settings/update.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Admin-Key': ADMIN_KEY
        },
        body: JSON.stringify(formData)
      });
      
      const data = await response.json();
      
      if (data.success) {
        onUpdateSettings(formData);
        setSuccessMessage('Site settings updated successfully!');
        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        setError(data.error || 'Failed to save settings');
      }
    } catch (error) {
      setError('Connection error. Please try again.');
      console.error('Error saving settings:', error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="pt-44 pb-12 min-h-screen bg-stone-50">
      <div className="max-w-6xl mx-auto px-6">
        
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-center mb-8 gap-4">
          <div>
            <button onClick={() => onNavigate('admin-dashboard')} className="flex items-center gap-2 text-stone-500 hover:text-stone-900 text-xs uppercase tracking-widest mb-2 transition-colors">
              <ArrowLeft size={14} /> Back to Dashboard
            </button>
            <h1 className="font-serif text-3xl text-stone-900">Site Configuration</h1>
            <p className="text-stone-500 font-light mt-2 text-sm">Update contact details and bank accounts displayed across the website.</p>
          </div>
        </div>

        {/* Success Message */}
        {successMessage && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700 flex items-center gap-2">
            <Check size={18} />
            {successMessage}
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-600 flex items-center gap-2">
            <AlertCircle size={18} />
            {error}
          </div>
        )}

        {/* Site Settings Section */}
        <div className="bg-white border border-stone-200 shadow-xl rounded-sm overflow-hidden mb-8">
          <div className="px-8 py-6 border-b border-stone-100 bg-stone-50 flex items-center gap-3">
            <Building size={20} className="text-gold-600" />
            <h2 className="font-serif text-xl text-stone-900">General Information</h2>
          </div>
          
          <form onSubmit={handleSiteSubmit} className="p-8 space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-2">
                <label className="text-xs uppercase tracking-widest text-stone-500 font-bold flex items-center gap-2">
                  <Mail size={14} /> Official Email
                </label>
                <input 
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full border border-stone-300 px-4 py-3 text-sm focus:border-gold-500 focus:outline-none rounded-sm bg-white transition-shadow focus:shadow-sm"
                />
                <p className="text-[10px] text-stone-400">Displayed in Footer and Contact Page</p>
              </div>

              <div className="space-y-2">
                <label className="text-xs uppercase tracking-widest text-stone-500 font-bold flex items-center gap-2">
                  <Phone size={14} /> Contact Number
                </label>
                <input 
                  type="text"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full border border-stone-300 px-4 py-3 text-sm focus:border-gold-500 focus:outline-none rounded-sm bg-white transition-shadow focus:shadow-sm"
                />
              </div>

              <div className="space-y-2 md:col-span-2">
                <label className="text-xs uppercase tracking-widest text-stone-500 font-bold flex items-center gap-2">
                  <MapPin size={14} /> Atelier Address
                </label>
                <input 
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  className="w-full border border-stone-300 px-4 py-3 text-sm focus:border-gold-500 focus:outline-none rounded-sm bg-white transition-shadow focus:shadow-sm"
                />
              </div>

              <div className="space-y-2 md:col-span-2">
                <label className="text-xs uppercase tracking-widest text-stone-500 font-bold flex items-center gap-2">
                  <Clock size={14} /> Operating Hours
                </label>
                <input 
                  type="text"
                  name="hours"
                  value={formData.hours}
                  onChange={handleChange}
                  className="w-full border border-stone-300 px-4 py-3 text-sm focus:border-gold-500 focus:outline-none rounded-sm bg-white transition-shadow focus:shadow-sm"
                />
              </div>
            </div>

            <div className="border-t border-stone-100 pt-8 flex justify-end">
              <button 
                type="submit"
                disabled={isSaving}
                className="px-8 py-3 bg-stone-900 text-white text-xs uppercase tracking-widest hover:bg-gold-600 transition-colors shadow-lg flex items-center gap-2 disabled:opacity-70"
              >
                {isSaving ? 'Updating...' : 'Save Configuration'}
                <Save size={16} />
              </button>
            </div>
          </form>
        </div>

        {/* Bank Accounts Section */}
        <div className="bg-white border border-stone-200 shadow-xl rounded-sm overflow-hidden">
          <div className="px-8 py-6 border-b border-stone-100 bg-stone-50 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CreditCard size={20} className="text-gold-600" />
              <h2 className="font-serif text-xl text-stone-900">Bank Accounts</h2>
            </div>
            
            <button
              onClick={() => setShowAddForm(!showAddForm)}
              className="flex items-center gap-2 bg-gold-600 text-white px-4 py-2 rounded-lg hover:bg-gold-700 transition-colors text-sm"
            >
              <Plus size={16} />
              {showAddForm ? 'Cancel' : 'Add Bank'}
            </button>
          </div>

          {/* Add/Edit Bank Form */}
          {showAddForm && (
            <div className="p-8 border-b border-stone-200 bg-stone-50/50">
              <h3 className="font-serif text-lg mb-4">{editingId ? 'Edit Bank Account' : 'Add New Bank Account'}</h3>
              
              <form onSubmit={handleBankSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs uppercase tracking-wider text-stone-500 font-bold mb-1">
                      Bank Name <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <Building size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                      <input
                        type="text"
                        name="bank_name"
                        value={bankForm.bank_name}
                        onChange={handleBankInputChange}
                        className="w-full pl-10 pr-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-gold-600 focus:border-transparent"
                        placeholder="e.g., BPI, BDO, Metrobank"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs uppercase tracking-wider text-stone-500 font-bold mb-1">
                      Branch (Optional)
                    </label>
                    <input
                      type="text"
                      name="branch"
                      value={bankForm.branch}
                      onChange={handleBankInputChange}
                      className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-gold-600 focus:border-transparent"
                      placeholder="e.g., Main Branch"
                    />
                  </div>

                  <div>
                    <label className="block text-xs uppercase tracking-wider text-stone-500 font-bold mb-1">
                      Account Number <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <Hash size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                      <input
                        type="text"
                        name="account_number"
                        value={bankForm.account_number}
                        onChange={handleBankInputChange}
                        className="w-full pl-10 pr-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-gold-600 focus:border-transparent"
                        placeholder="1234-5678-90"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs uppercase tracking-wider text-stone-500 font-bold mb-1">
                      Account Name <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                      <input
                        type="text"
                        name="account_name"
                        value={bankForm.account_name}
                        onChange={handleBankInputChange}
                        className="w-full pl-10 pr-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-gold-600 focus:border-transparent"
                        placeholder="Sparkling Gems Inc."
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs uppercase tracking-wider text-stone-500 font-bold mb-1">
                      Currency
                    </label>
                    <div className="relative">
                      <DollarSign size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                      <select
                        name="currency"
                        value={bankForm.currency}
                        onChange={handleBankInputChange}
                        className="w-full pl-10 pr-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-gold-600 focus:border-transparent"
                      >
                        <option value="PHP">PHP - Philippine Peso</option>
                        <option value="USD">USD - US Dollar</option>
                        <option value="EUR">EUR - Euro</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs uppercase tracking-wider text-stone-500 font-bold mb-1">
                      SWIFT Code (Optional)
                    </label>
                    <div className="relative">
                      <Globe size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                      <input
                        type="text"
                        name="swift_code"
                        value={bankForm.swift_code}
                        onChange={handleBankInputChange}
                        className="w-full pl-10 pr-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-gold-600 focus:border-transparent"
                        placeholder="BOPIPHMM"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs uppercase tracking-wider text-stone-500 font-bold mb-1">
                      Routing Number (Optional)
                    </label>
                    <input
                      type="text"
                      name="routing_number"
                      value={bankForm.routing_number}
                      onChange={handleBankInputChange}
                      className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-gold-600 focus:border-transparent"
                      placeholder="021000021"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-xs uppercase tracking-wider text-stone-500 font-bold mb-1">
                      Payment Instructions
                    </label>
                    <textarea
                      name="instructions"
                      value={bankForm.instructions}
                      onChange={handleBankInputChange}
                      rows={3}
                      className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-gold-600 focus:border-transparent"
                      placeholder="After making the transfer, please upload your proof of payment below..."
                    />
                  </div>

                  <div className="flex items-center gap-6">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        name="is_active"
                        checked={bankForm.is_active}
                        onChange={(e) => setBankForm({ ...bankForm, is_active: e.target.checked })}
                        className="w-4 h-4 text-gold-600 rounded"
                      />
                      <span className="text-sm text-stone-700">Active</span>
                    </label>

                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        name="is_default"
                        checked={bankForm.is_default}
                        onChange={(e) => setBankForm({ ...bankForm, is_default: e.target.checked })}
                        className="w-4 h-4 text-gold-600 rounded"
                      />
                      <span className="text-sm text-stone-700">Set as Default</span>
                    </label>
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="submit"
                    className="flex items-center gap-2 bg-gold-600 text-white px-6 py-2 rounded-lg hover:bg-gold-700 transition-colors"
                  >
                    <Save size={18} />
                    {editingId ? 'Update' : 'Save'} Bank
                  </button>
                  <button
                    type="button"
                    onClick={resetBankForm}
                    className="flex items-center gap-2 border border-stone-300 px-6 py-2 rounded-lg hover:bg-stone-50 transition-colors"
                  >
                    <X size={18} />
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Bank List */}
          <div className="p-8">
            {loadingBanks ? (
              <div className="text-center py-12">
                <div className="w-10 h-10 border-3 border-gold-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-stone-500">Loading bank accounts...</p>
              </div>
            ) : banks.length === 0 ? (
              <div className="text-center py-12 bg-stone-50 rounded-lg border border-dashed border-stone-300">
                <Building size={48} className="mx-auto mb-4 text-stone-400" />
                <p className="text-stone-500">No bank accounts added yet</p>
                <p className="text-xs text-stone-400 mt-2">Click "Add Bank" to create your first bank account</p>
              </div>
            ) : (
              <div className="space-y-4">
                {banks.map((bank) => (
                  <div key={bank.id} className="border border-stone-200 rounded-lg p-4 hover:shadow-sm transition-shadow">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-serif text-lg">{bank.bank_name}</h3>
                          {bank.is_default && (
                            <span className="bg-gold-100 text-gold-700 text-[10px] px-2 py-1 rounded-full font-bold">DEFAULT</span>
                          )}
                          {!bank.is_active && (
                            <span className="bg-stone-100 text-stone-500 text-[10px] px-2 py-1 rounded-full">INACTIVE</span>
                          )}
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                          <div>
                            <p className="text-[10px] uppercase text-stone-400">Account Number</p>
                            <p className="font-mono font-medium">{bank.account_number}</p>
                          </div>
                          <div>
                            <p className="text-[10px] uppercase text-stone-400">Account Name</p>
                            <p className="font-medium">{bank.account_name}</p>
                          </div>
                          {bank.branch && (
                            <div>
                              <p className="text-[10px] uppercase text-stone-400">Branch</p>
                              <p>{bank.branch}</p>
                            </div>
                          )}
                          <div>
                            <p className="text-[10px] uppercase text-stone-400">Currency</p>
                            <p>{bank.currency}</p>
                          </div>
                          {bank.swift_code && (
                            <div>
                              <p className="text-[10px] uppercase text-stone-400">SWIFT</p>
                              <p className="font-mono">{bank.swift_code}</p>
                            </div>
                          )}
                          {bank.routing_number && (
                            <div>
                              <p className="text-[10px] uppercase text-stone-400">Routing</p>
                              <p className="font-mono">{bank.routing_number}</p>
                            </div>
                          )}
                        </div>

                        {bank.instructions && (
                          <div className="mt-3 p-3 bg-stone-50 rounded-lg text-sm text-stone-600">
                            <p className="text-[10px] uppercase text-stone-400 mb-1">Instructions</p>
                            <p>{bank.instructions}</p>
                          </div>
                        )}
                      </div>

                      <div className="flex items-center gap-2 ml-4">
                        <button
                          onClick={() => handleEditBank(bank)}
                          className="p-2 text-stone-400 hover:text-blue-600 transition-colors"
                          title="Edit"
                        >
                          <Edit2 size={16} />
                        </button>
                        {!bank.is_default && (
                          <button
                            onClick={() => handleDeleteBank(bank.id!)}
                            className="p-2 text-stone-400 hover:text-red-600 transition-colors"
                            title="Delete"
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;